import React, { useState } from 'react';
import { Summary } from '@ajat/design-system/ShoppingCart';
import { ComponentPage, DemoCard } from '../../shared';

export const meta = {
  component: Summary,
  title: 'Summary',
  group: 'Shopping Cart',
  importPath: '@ajat/design-system/ShoppingCart',
  figmaNode: '3959:1595',
  description:
    'Order total card: delivery, dynamic line items, discount rows, total, CTA, a risk-free promo and the discount-code footer. variant="panel" is the OVER/LL cart skin (OVER-LL 2762:26499): flat, the page column is the surface, the discount code in its own card. It composes the Discount component, so a cart page never renders both itself.',
  props: [
    ['title', 'string', "'Summary'", 'Card heading'],
    ['deliveryTime', 'string', "'3 Weeks'", 'Delivery time'],
    ['deliveryCost', 'string', "'Free'", 'Delivery cost label'],
    ['deliveryDescription', 'string', "'Delivery to your school'", 'Delivery description'],
    ['deliveryPrice', 'string', "'0:-'", 'Right-aligned delivery price'],
    ['lineItems', 'array', '[]', '[{ label, price }] — Products, Packages, and so on'],
    ['productsLabel', 'string', "'Products'", 'Legacy single-line label, used when lineItems is empty'],
    ['productsPrice', 'string', '—', 'Legacy single-line price'],
    ['discounts', 'array', '[]', '[{ label, amount }] — each renders as a green row'],
    ['totalLabel', 'string', "'Total'", 'Total row label'],
    ['totalPrice', 'string', '—', 'Final total (required)'],
    ['originalPrice', 'string', "''", 'Struck-through pre-discount total'],
    ['ctaLabel', 'string', "'Go to checkout'", 'CTA label'],
    ['onCtaClick', 'func', '—', 'CTA handler'],
    ['ctaLoading', 'bool', 'false', 'CTA spinner'],
    ['ctaDisabled', 'bool', 'false', 'Disables the CTA'],
    ['ctaSize', 'string', "'primary'", 'Button size of the CTA'],
    ['ctaVariant', 'string', "'alternative'", 'Button variant of the CTA'],
    ['ctaClassName', 'string', "''", 'Extra class on the CTA (placement only; the OVER/LL cart passes `ctaVariant="hero"`)'],
    ['promoTitle', 'string', "'BUY RISK FREE'", 'Promo heading'],
    ['promoSubtitle', 'string', "'24 HOURS TO EDIT…'", 'Promo subtitle'],
    ['showPromo', 'bool', 'true', 'Show the risk-free promo — hide it for overall carts, which have no edit window'],
    ['showDiscountCode', 'bool', 'true', 'Show the discount-code footer'],
    ['discountCodeLabel', 'string', "'Discount code'", 'Label of the discount field'],
    ['discountCode', 'string', "''", 'Applied code'],
    ['discountSubmitted', 'bool', 'false', 'Code applied — renders the read-only Discount row'],
    ['discountLabel', 'string', "''", 'Green success message'],
    ['discountErrorCode', 'string', "''", 'Invalid code, pre-filled back into the field'],
    ['discountErrorMessage', 'string', "''", 'Red error message'],
    ['onDiscountSubmit', 'func', '—', 'Fires with a newly entered code'],
    ['onDiscountRemove', 'func', '—', 'Fires when the applied code is removed'],
    ['variant', "'card' | 'panel'", "'card'", 'card = bordered card (Clothing carts); panel = flat, no surface of its own, discount code in its own card (OVER/LL cart)'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  usage: [
    {
      title: 'A cart sidebar',
      code: `import { Summary } from '@ajat/design-system/ShoppingCart';

<Summary
  lineItems={[
    { label: 'Produkter', price: \`\${productsTotal},-\` },
    { label: 'Pakker', price: \`\${packagesTotal},-\` },
  ]}
  discounts={discount ? [{ label: \`Rabat \${discount.percent}%\`, amount: \`-\${discountAmount},-\` }] : []}
  totalPrice={\`\${total},-\`}
  originalPrice={discount ? \`\${subtotal},-\` : ''}
  ctaLabel="Til betaling"
  onCtaClick={() => navigate('/clothing/cls-dk/checkout')}
  showPromo={!hasOverall}          // overalls carry no edit window
  discountCode={discount?.code ?? ''}
  discountSubmitted={Boolean(discount)}
  onDiscountSubmit={applyDiscount}
  onDiscountRemove={removeDiscount}
/>`,
    },
  ],
};

export default function SummaryPage() {
  const [code, setCode] = useState('SHOP15');
  const [submitted, setSubmitted] = useState(true);

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="With a discount applied" surface={false}>
        <div className="max-w-md">
          <Summary
            lineItems={[
              { label: 'Products', price: '1 469:-' },
              { label: 'Package Deal', price: '500:-' },
            ]}
            discounts={[{ label: 'Discount 15%', amount: '-220:-' }]}
            totalPrice="1 749:-"
            originalPrice="1 969:-"
            discountCode={code}
            discountSubmitted={submitted}
            discountLabel="15% DISCOUNT"
            onDiscountSubmit={(c) => { setCode(c); setSubmitted(true); }}
            onDiscountRemove={() => { setCode(''); setSubmitted(false); }}
          />
        </div>
      </DemoCard>

      <DemoCard title='variant="panel"' hint="OVER/LL cart — flat; the page column is the surface, the discount code its own card">
        <div className="max-w-md">
          <Summary
            variant="panel"
            lineItems={[{ label: 'Products', price: '1 220:-' }]}
            totalPrice="1 220:-"
            showPromo={false}
            onDiscountSubmit={() => {}}
          />
        </div>
      </DemoCard>

            <DemoCard title="Invalid code" surface={false}>
        <div className="max-w-md">
          <Summary
            lineItems={[{ label: 'Products', price: '1 469:-' }]}
            totalPrice="1 469:-"
            discountErrorCode="WRONG"
            discountErrorMessage="INVALID CODE"
            onDiscountSubmit={() => {}}
          />
        </div>
      </DemoCard>

      <DemoCard title="No discount field, no promo" surface={false}>
        <div className="max-w-md">
          <Summary
            lineItems={[{ label: 'Produkter', price: '899,-' }]}
            totalPrice="899,-"
            deliveryTime="3 uger"
            deliveryCost="Gratis"
            deliveryDescription="Levering til din skole"
            totalLabel="Total"
            ctaLabel="Til betaling"
            showPromo={false}
            showDiscountCode={false}
          />
        </div>
      </DemoCard>

      {/* The pre-lineItems API. Still supported, so it stays documented. */}
      <DemoCard title="Legacy single line" hint="productsLabel / productsPrice, used when lineItems is empty" surface={false}>
        <div className="max-w-md">
          <Summary
            productsLabel="Products"
            productsPrice="1 469:-"
            totalPrice="1 469:-"
            showDiscountCode={false}
          />
        </div>
      </DemoCard>

      <DemoCard title="CTA states" surface={false}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[var(--space-ui-medium-m)]">
          <Summary
            lineItems={[{ label: 'Products', price: '449:-' }]}
            totalPrice="449:-"
            ctaLoading
            showPromo={false}
            showDiscountCode={false}
          />
          <Summary
            lineItems={[{ label: 'Products', price: '0:-' }]}
            totalPrice="0:-"
            ctaDisabled
            showPromo={false}
            showDiscountCode={false}
          />
        </div>
      </DemoCard>
    </ComponentPage>
  );
}
